import * as React from 'react'
import { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, type IntlShape } from 'react-intl'
import moment from 'moment'

import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import NestableClickable from 'components/Common/NestableClickable'
import OptionsButton from 'tripPlanner/components/Documents/OptionsButton'
import { OptionsMenuActions, OptionsMenuButton } from 'tripPlanner/components/Documents/OptionsMenuActions'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import RecentItemInlineTitleEdit, { type RecentItemTitleVariant } from './RecentItemInlineTitleEdit'
import useBeginRecentItemRename from './useBeginRecentItemRename'
import useOpenRecentChatItemActionsSheet from './useOpenRecentChatItemActionsSheet'

const NOOP_MOUSE_EVENT = {
  preventDefault: () => undefined,
  stopPropagation: () => undefined,
} as React.MouseEvent

export interface AssistantHistoryListItemData {
  type: 'aiChat' | 'classicTrip'
  id: number | string
  title?: string
  subtitle?: string
  description?: string
  updatedAt: string
}

const Container = styled(NestableClickable)`
  display: block;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding: ${rem(12)} 0;

  &:last-child {
    border-bottom: none;
  }

  &.active,
  &:hover {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

const Content = styled.div`
  display: flex;
  gap: ${rem(8)};
  align-items: flex-start;
  justify-content: space-between;
  min-width: 0;
`

const TextContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding-inline-start: ${rem(20)};
`

const Description = styled(BodyText)`
  margin-block-start: ${rem(4)};
`

const LastActivity = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: baseline;
  column-gap: ${rem(4)};
  width: 100%;
  min-width: 0;
  margin-block-start: ${rem(6)};

  &.with-separator {
    grid-template-columns: auto auto minmax(0, 1fr);
  }
`

const LastMessage = styled(BodyText)`
  min-width: 0;
`

const Separator = styled.span`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  line-height: ${rem(16)};

  &::before {
    content: '·';
  }
`

interface Props<TItem extends AssistantHistoryListItemData> {
  item: TItem
  subtitle?: string
  description?: string
  to?: string
  active?: boolean
  disabled?: boolean
  hideActions?: boolean
  titleVariant?: RecentItemTitleVariant
  onClick?: React.MouseEventHandler
  onRename: (item: TItem, name: string) => Promise<void>
  onDeleteClick: (item: TItem, closeMenu: () => void) => (event: React.MouseEvent) => void
}

export function getCompactRecentChatTimestampLabel(updatedAt: string, intl: IntlShape, now = moment()): string {
  const updatedAtMoment = moment(updatedAt)
  if (!updatedAtMoment.isValid()) {
    return intlDateFormatter(updatedAt, INTL_DAY_MONTH_NAME_SHORT, intl.locale)
  }

  const dateFormat = updatedAtMoment.year() === now.year() ? INTL_DAY_MONTH_NAME_SHORT : INTL_DMY_CASUAL_SHORT_FORMAT
  if (updatedAtMoment.isAfter(now)) {
    return intlDateFormatter(updatedAt, dateFormat, intl.locale)
  }

  const elapsed = moment.duration(now.diff(updatedAtMoment))
  const elapsedMinutes = elapsed.asMinutes()
  const elapsedHours = elapsed.asHours()
  const daysAgo = now.diff(updatedAtMoment, 'days')

  if (elapsedMinutes < 60) {
    return intl.formatMessage(
      {
        id: 'ypnHv6MlPJK7huj2MeOn',
        defaultMessage: '{count}m',
        description: 'Compact minutes-since label in the Trip Planner chat drawer, such as 1m or 34m',
      },
      { count: Math.max(1, Math.floor(elapsedMinutes)) },
    )
  }

  if (elapsedHours < 24) {
    return intl.formatMessage(
      {
        id: 'r3w7lFVyL-rGfdUBXZyf',
        defaultMessage: '{count}h',
        description: 'Compact hours-since label in the Trip Planner chat drawer, such as 1h or 7h',
      },
      { count: Math.max(1, Math.min(23, Math.round(elapsedHours))) },
    )
  }

  if (daysAgo <= 7) {
    return intl.formatMessage(
      {
        id: 'UopsFCaBxwLeFeITP77a',
        defaultMessage: '{count}d',
        description: 'Compact days-since label in the Trip Planner chat drawer, such as 1d or 7d',
      },
      { count: daysAgo },
    )
  }

  return intlDateFormatter(updatedAt, dateFormat, intl.locale)
}

export default function AssistantHistoryListItem<TItem extends AssistantHistoryListItemData>({
  item,
  subtitle,
  description,
  to,
  active = false,
  disabled = false,
  hideActions = false,
  titleVariant = 'medium',
  onClick,
  onRename,
  onDeleteClick,
}: Props<TItem>) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const fallbackTitle = intl.formatMessage({
    id: 'P7xyHLCCjSlOklqXsVYg',
    defaultMessage: 'Untitled',
    description: 'Default title for untitled trip in the trip planner feature',
  })
  const {
    isRenaming,
    setIsRenaming,
    titleEditRef,
    beginRename,
    displayTitle,
    handleRenamed,
  } = useBeginRecentItemRename(item.title, fallbackTitle)
  const formattedTimestamp = getCompactRecentChatTimestampLabel(item.updatedAt, intl)
  const actionsTitle = item.type === 'classicTrip' ?
      intl.formatMessage({
        id: '3VJ6QlvfdQdt7iHnZ58y',
        defaultMessage: 'Trip actions',
        description: 'Accessible title for the trip options menu button in the drawer',
      }) :
      intl.formatMessage({
        id: '-bD6A4Qe-0a_1LR2lLWn',
        defaultMessage: 'Chat actions',
        description: 'Accessible title for the chat actions dropdown menu in the drawer list item',
      })
  const actionMenuAriaLabel = intl.formatMessage({
    id: 'OSwxcyWZf8-szg3KOL5Q',
    defaultMessage: 'action menu',
    description: 'Accessible label for the document options menu button',
  })
  const deleteLabel = item.type === 'classicTrip' ?
      intl.formatMessage({
        id: 'Jp_lIRegyPJ1-xyn0DHR',
        defaultMessage: 'Delete',
        description: 'Delete button label in the trip actions menu in the drawer',
      }) :
      intl.formatMessage({
        id: 'NYvcrggtkoIHuLPifYLV',
        defaultMessage: 'Delete',
        description: 'Button label to delete a chat conversation from the TripPlanner drawer list',
      })

  const renameLabel = intl.formatMessage({
    id: 'nxJehzKwdB_IfLuVEgrw',
    defaultMessage: 'Rename',
    description: 'Button label to rename a chat or trip from the TripPlanner drawer list',
  })

  const handleRename = useCallback(async(name: string) => {
    await onRename(item, name)
    handleRenamed(name)
  }, [handleRenamed, item, onRename])

  const handleSheetDelete = useCallback(() => {
    onDeleteClick(item, () => undefined)(NOOP_MOUSE_EVENT)
  }, [item, onDeleteClick])

  const openActionsSheet = useOpenRecentChatItemActionsSheet({
    itemType: item.type,
    title: displayTitle,
    onRename: handleRename,
    onDelete: handleSheetDelete,
  })

  const handleRenameClick = (closeMenu: () => void) => (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    beginRename(closeMenu)
  }

  const isDesktopRenaming = !isMobile && isRenaming
  const lastActivityClassName = formattedTimestamp && subtitle ? 'with-separator' : undefined

  return (
    <Container
      to={isDesktopRenaming ? undefined : to}
      onClick={isDesktopRenaming ? undefined : onClick}
      className={active ? 'active' : undefined}
      disabled={disabled}
    >
      <Content>
        <TextContent>
          <RecentItemInlineTitleEdit
            ref={titleEditRef}
            title={displayTitle}
            fallbackTitle={fallbackTitle}
            isEditing={isDesktopRenaming}
            variant={titleVariant}
            onEditingChange={setIsRenaming}
            onRename={handleRename}
          />
          {!!description && (
            <Description variant="small" colour="normal-contrast" lineClamp={2}>
              {description}
            </Description>
          )}
          {(formattedTimestamp || subtitle) && (
            <LastActivity className={lastActivityClassName}>
              {!!formattedTimestamp && (
                <BodyText variant="small" colour="light-foreground-alt" as="span">
                  {formattedTimestamp}
                </BodyText>
              )}
              {!!formattedTimestamp && !!subtitle && (
                <Separator aria-hidden />
              )}
              {!!subtitle && (
                <LastMessage variant="small" colour="light-foreground-alt" italics lineClamp={1}>
                  {subtitle}
                </LastMessage>
              )}
            </LastActivity>
          )}
        </TextContent>
        {!hideActions && (
          isMobile ? (
            <IconButton
              kind="tertiary"
              shape="circle"
              size="small"
              data-testid="action-menu-button"
              aria-label={actionMenuAriaLabel}
              title={actionsTitle}
              onClick={openActionsSheet}
            >
              <LineEllipsisVIcon />
            </IconButton>
          ) : (
            <OptionsButton
              title={actionsTitle}
              bodyContent={(closeMenu) => (
                <OptionsMenuActions>
                  <OptionsMenuButton
                    type="button"
                    onClick={handleRenameClick(closeMenu)}
                  >
                    {renameLabel}
                  </OptionsMenuButton>
                  <OptionsMenuButton type="button" onClick={onDeleteClick(item, closeMenu)}>
                    {deleteLabel}
                  </OptionsMenuButton>
                </OptionsMenuActions>
              )}
            />
          )
        )}
      </Content>
    </Container>
  )
}
